import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables, isDefined, numberOrZero } from "@/utils/utils";
import { Card, Divider, Popover, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useDownloadBailAccountsExport, useGetBailAccountsReport } from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { ExportFileType } from "../../../../../common/enums";
import { SuperiorAgentsTable } from "../../../../agent/components/tables/SuperiorAgentsTable";
import { AgentTypeTagWithName } from "../../../../agent/components/tags/AgentTypeTagWithName";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { formatAgentIdNumber } from "../../../../agent/utils";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { BailAccountBalance, BailAccountsReportFilterPageRequest } from "../../types";

type Props = {
  searchParams: BailAccountsReportFilterPageRequest;
};

export const BailAccountsReportTableView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data } = useGetBailAccountsReport(searchParams);
  const downloadBailAccountsExport = useDownloadBailAccountsExport();

  const columns: ColumnsType<BailAccountBalance> = [
    {
      key: "agentName",
      title: t("common.agent"),
      width: 220,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <AgentTypeTagWithName agent={record.agent} hideIdNumber ellipsis showLink showRepresentatives />
      ),
    },
    {
      key: "idNumber",
      title: t("agent.attrs.idNumber"),
      width: 100,
      render: (_, record) => formatAgentIdNumber(record.agent),
    },
    {
      key: "commissionsSettingsLevel",
      title: t("agent.helpers.commissionsSettingsLevel"),
      width: 100,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.level?.code}</Ellipsis>,
    },
    {
      key: "commissionsSettingsRank",
      title: t("agent.helpers.commissionsSettingsRank"),
      width: 150,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.rank?.name}</Ellipsis>,
    },
    {
      key: "superiorAgents",
      width: 40,
      align: "center",
      render: (_, record) => (
        <Popover
          title={t("agent.titles.superiorAgents")}
          styles={{ root: { maxWidth: 580 } }}
          content={<SuperiorAgentsTable agent={record.agent} />}
        >
          <span>
            <AntIcon color="blue" type="cluster" />
          </span>
        </Popover>
      ),
    },
    {
      key: "status",
      title: (
        <div>
          <div>{isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}</div>
          {isTopAgentPlus ? <div>{t("agent.helpers.agentSecondaryStatusSFAPlusLabel")}</div> : undefined}
        </div>
      ),
      width: 210,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <>
          <Ellipsis>{t(`agent.enums.agentStatus.${record.agent.status}`)}</Ellipsis>

          {isTopAgentPlus && record.agent.secondaryStatus ? (
            <>
              <br />
              <Ellipsis>{t(`agent.enums.agentSecondaryStatus.${record.agent.secondaryStatus}`)}</Ellipsis>
            </>
          ) : undefined}
        </>
      ),
    },
    {
      key: "targetAmount",
      title: t("commissions.bailAccount.attrs.targetAmount"),
      align: "right",
      width: 120,
      render: (_, record) => (isDefined(record.targetAmount) ? formatLocaleCurrency(record.targetAmount) : undefined),
    },
    {
      key: "balance",
      title: t("commissions.bailAccount.helpers.balance"),
      align: "right",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <span
          style={
            record.targetAmount && record.balance >= record.targetAmount
              ? { fontWeight: "bold", color: cssVariables.colorGreen }
              : undefined
          }
        >
          {formatLocaleCurrency(record.balance)}
        </span>
      ),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <ActionTextIcon
          path={generatePath(AGENT_ROUTE_PATHS.commissions.to, { id: record.agent.id }) + "?tab=bail-account"}
          icon="eye"
          color="blue"
          text={t("common.show")}
        />
      ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card className="card-box">
      <div className={numberOrZero(data?.totalElementsCount) ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() => downloadBailAccountsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })}
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() => downloadBailAccountsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })}
        />
      </div>

      <Table<BailAccountBalance>
        {...tableStandardProps()}
        scroll={{ x: TableSizes.LARGE }}
        rowKey={(record) => record.agent.id}
        columns={columns}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};
